/**
 * Ontology domain event schemas.
 *
 * Emitted when the shared knowledge graph changes. The Postgres triggers in
 * `20260710000000_ontology.sql` emit these events; apps can also emit them
 * manually for in-memory ontology updates.
 *
 * Rule: fields added after initial release MUST be `.optional()`.
 *
 * @module ontology.events
 * @since 2026-07-10
 */

import { z } from 'zod'
import { BackboneEventSchema } from '../types/backbone.ts'
import {
  OntologyConceptSchema,
  OntologyRelationSchema,
  OntologyAssertionSchema,
} from '../ontology.ts'

export const OntologyConceptDefinedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('concept_defined'),
  payload: OntologyConceptSchema,
})

export const OntologyConceptUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('concept_updated'),
  payload: OntologyConceptSchema,
})

export const OntologyConceptDeletedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('concept_deleted'),
  payload: OntologyConceptSchema,
})

export const OntologyRelationDefinedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('relation_defined'),
  payload: OntologyRelationSchema,
})

export const OntologyRelationUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('relation_updated'),
  payload: OntologyRelationSchema,
})

export const OntologyRelationDeletedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('relation_deleted'),
  payload: OntologyRelationSchema,
})

export const OntologyAssertionCreatedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('assertion_created'),
  payload: OntologyAssertionSchema,
})

export const OntologyAssertionUpdatedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('assertion_updated'),
  payload: OntologyAssertionSchema,
})

export const OntologyAssertionRetractedEvent = BackboneEventSchema.extend({
  domain: z.literal('ontology'),
  type: z.literal('assertion_retracted'),
  payload: OntologyAssertionSchema,
})

export const OntologyDomainEvents = [
  OntologyConceptDefinedEvent,
  OntologyConceptUpdatedEvent,
  OntologyConceptDeletedEvent,
  OntologyRelationDefinedEvent,
  OntologyRelationUpdatedEvent,
  OntologyRelationDeletedEvent,
  OntologyAssertionCreatedEvent,
  OntologyAssertionUpdatedEvent,
  OntologyAssertionRetractedEvent,
] as const
